/**
 * ProposalDetail — modal content for a proposal order.
 *
 * State machine rendering (F-2.0 Plan Phase 5+7):
 * - No revision yet  → ProposalEditor (new, version=0)
 * - DRAFT            → ProposalEditor (edit) + ProposalActions (send) — actions always visible
 * - DRAFT_SENT       → ProposalRevisionView (read-only) + ProposalActions (accept/decline)
 * - CHANGE_REQUESTED → ProposalRevisionView + change-request justification banner + ProposalActions (revise)
 * - In revise mode   → ProposalEditor pre-filled from latest + POST /revise
 * - ACCEPTED/DECLINED/EXPIRED → ProposalRevisionView (read-only, no actions)
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { OmsOrder, parseCustomerDataBlob } from "../../model/Oms";
import {
  ProposalStatus,
  AdminRevisionDTO,
  ProposalResponse,
  isEditable,
  canRevise,
} from "../../model/Proposal";
import { buildProposalResponse } from "../../helpers/proposalApi";
import { orderShortRef } from "../../helpers/orderRef";
import ProposalStatusChip from "./ProposalStatusChip";
import ProposalRevisionView from "./ProposalRevisionView";
import ProposalEditor from "./ProposalEditor";
import ProposalActions from "./ProposalActions";

interface Props {
  order: OmsOrder;
  /** Authenticated user — needed to derive the discriminated `ProposalResponse`
   *  role at the API boundary. Passed through from `OmsProposalsPage` rather
   *  than read from a context to keep the component testable and the role
   *  source explicit (JWT-derived, never trust the wire). */
  userProfile: ProfileAndSettings;
  onOrderUpdated: (updated: OmsOrder) => void;
}

const ProposalDetail: React.FC<Props> = ({
  order,
  userProfile,
  onOrderUpdated,
}) => {
  const quoteCustomer = parseCustomerDataBlob(order.customer_data);
  const [revision, setRevision] = useState<AdminRevisionDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"view" | "edit" | "revise">("view");
  // Tracks whether the initial mode has been set from server data.
  // Subsequent fetchRevision calls (after save/revise/status-change) must NOT
  // override the mode that was explicitly set by the action handler.
  const initialModeSet = useRef(false);

  // Local copies of proposal_status and proposal_version — updated immediately
  // by action handlers so the UI reacts without waiting for the parent to
  // re-render (the modal JSX is captured once at open time, so the `order` prop
  // is stale after any mutation).
  const [localStatus, setLocalStatus] = useState<ProposalStatus | null>(
    order.proposal_status ?? null,
  );
  const [localVersion, setLocalVersion] = useState<number | undefined>(
    order.proposal_version ?? undefined,
  );

  const proposalStatus = localStatus;

  /** Customer preview link — from the fetched revision (survives reload,
   *  OMS re-issues it when DRAFT_SENT) or directly after send. */
  const previewUrl = revision?.preview_url ?? null;

  /** Discriminated `ProposalResponse` — role derived from JWT via
   *  `buildProposalResponse`; comercial branch strips admin-only fields.
   *  Recomputed only when the fetched `revision` or `userProfile.profile` changes. */
  const response = useMemo<ProposalResponse | null>(() => {
    if (!revision) return null;
    return buildProposalResponse(userProfile.profile, revision);
  }, [revision, userProfile.profile]);

  const fetchRevision = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<AdminRevisionDTO>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}/proposal`,
      );
      setRevision(res.data);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setRevision(null); // no revision yet — show blank editor
      } else {
        setError(e?.response?.data?.error ?? "Erro ao carregar proposta.");
      }
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => {
    fetchRevision();
  }, [fetchRevision]);

  // After saving a new revision → refresh + switch to view
  const handleRevisionSaved = (newVersion: number) => {
    setLocalStatus("DRAFT");
    setLocalVersion(newVersion);
    onOrderUpdated({
      ...order,
      proposal_status: "DRAFT",
      proposal_version: newVersion,
    });
    fetchRevision();
    setMode("view");
  };

  // After "Iniciar revisão" → create revision via /revise endpoint
  const handleReviseStarted = async () => {
    if (!revision) return;
    try {
      const res = await api.post<{ version: number }>(
        `${API_URLS.OMS_ADMIN_ORDERS}/${order.id}/proposal/revise`,
        { source_version: revision.version },
      );
      setLocalStatus("DRAFT");
      setLocalVersion(res.data.version);
      onOrderUpdated({
        ...order,
        proposal_status: "DRAFT",
        proposal_version: res.data.version,
      });
      fetchRevision();
      setMode("edit");
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Erro ao iniciar revisão.");
    }
  };

  const handleStatusChanged = (newStatus: ProposalStatus) => {
    setLocalStatus(newStatus);
    onOrderUpdated({ ...order, proposal_status: newStatus });
    setMode("view");
    fetchRevision();
  };

  // Decide initial mode from status — runs only once on first load.
  // revision.version === 0 means synthetic draft (no saved revision yet) —
  // treat it the same as revision===null for mode decision purposes.
  const isSyntheticDraft = revision !== null && revision.version === 0;
  useEffect(() => {
    if (!loading && !initialModeSet.current) {
      initialModeSet.current = true;
      if ((!revision || isSyntheticDraft) && !proposalStatus) {
        setMode("edit"); // first ever save (null or synthetic v0)
      } else if (
        proposalStatus &&
        isEditable(proposalStatus) &&
        proposalStatus !== "CHANGE_REQUESTED"
      ) {
        setMode("edit"); // DRAFT → open editor
      } else {
        setMode("view");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box p={2} minWidth={560}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Proposta
        </Typography>
        <ProposalStatusChip status={proposalStatus} size="medium" />
        {localVersion != null && (
          <Chip
            label={`v${localVersion}`}
            size="small"
            variant="outlined"
            sx={{ fontFamily: "monospace" }}
          />
        )}
      </Box>

      {/* Order summary */}
      <Box
        sx={{
          bgcolor: "#f8f9fa",
          border: "1px solid #e9ecef",
          borderRadius: 1,
          p: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>Encomenda:</strong> {orderShortRef(order.id)} &nbsp;·&nbsp;
          <strong>Cliente:</strong> {order.customer_name} (
          {order.customer_email})
          {quoteCustomer.phone && (
            <>
              {" "}
              &nbsp;·&nbsp;
              <strong>Tel:</strong> {quoteCustomer.phone}
            </>
          )}
          {order.proposal_sent_at && (
            <>
              {" "}
              &nbsp;·&nbsp;
              <strong>Enviada:</strong>{" "}
              {new Date(order.proposal_sent_at).toLocaleString("pt-PT")}
            </>
          )}
        </Typography>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* EDIT mode */}
          {(mode === "edit" || mode === "revise") && (
            <>
              {mode === "revise" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Estás em modo revisão — os artigos foram pré-carregados da
                  versão anterior. Edita e guarda o novo rascunho.
                </Alert>
              )}
              {mode === "edit" && isSyntheticDraft && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Artigos pré-carregados do orçamento submetido pelo cliente.
                  Preenche o <strong>custo unitário</strong> de cada artigo e
                  ajusta os preços antes de guardar.
                </Alert>
              )}
              <ProposalEditor
                orderId={order.id}
                currentVersion={revision?.version ?? 0}
                initialItems={
                  revision
                    ? revision.items.map((it) => ({
                        _localId: crypto.randomUUID(),
                        product_id: it.product_id,
                        name: it.name,
                        reference: it.reference,
                        component_type: it.component_type,
                        // pvp_cents in the draft editor = the editable price.
                        // Prefer the saved selling price (unit_price_cents on
                        // the wire) so editing keeps the negotiated value;
                        // fall back to catalog PVP / rev3 for legacy payloads.
                        pvp_cents:
                          it.unit_price_cents ??
                          it.pvp_cents ??
                          it.rev3_cents ??
                          0,
                        cost_cents: it.cost_cents ?? 0,
                        quantity: it.quantity,
                        rev3_cents: it.rev3_cents,
                        rev4_cents: it.rev4_cents,
                      }))
                    : [] // backend synthesizes v0 drafts; empty fallback is unreachable with items
                }
                initialNotes={revision?.notes}
                initialReferenceType={revision?.reference_type}
                onSaved={handleRevisionSaved}
                onCancel={
                  revision && !isSyntheticDraft
                    ? () => setMode("view")
                    : undefined
                }
              />
            </>
          )}

          {/* VIEW mode + latest revision */}
          {mode === "view" && response && (
            <ProposalRevisionView
              response={response}
              showChangeRequestJustification={
                proposalStatus === "CHANGE_REQUESTED"
              }
            />
          )}

          {mode === "view" && !revision && !proposalStatus && (
            <Alert severity="info">
              Ainda não existe proposta para esta encomenda.
            </Alert>
          )}

          {/* Actions — shown in all modes so buttons are always accessible.
               In edit mode: ProposalActions shows "Enviar proposta" (DRAFT)
               In view mode: ProposalActions shows accept/decline/revise actions */}
          <>
            <Divider sx={{ my: 2 }} />
            <ProposalActions
              orderId={order.id}
              proposalStatus={proposalStatus}
              proposalVersion={localVersion}
              onStatusChanged={handleStatusChanged}
              onReviseStarted={
                canRevise(proposalStatus)
                  ? handleReviseStarted
                  : () => setMode("revise")
              }
              customerPhone={quoteCustomer.phone}
              customerName={order.customer_name}
              previewUrl={previewUrl}
            />
          </>
        </>
      )}
    </Box>
  );
};

export default ProposalDetail;
